import { Skeleton } from "../../../../components/ui/common/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from "../../../../components/ui/common/table";
import { cn } from "../../../../lib/utils";
import { IngredientRowViewModel } from "../types/ingredientListView.types";
import { INGREDIENT_TABLE_COLUMNS, ingredientTableColumnClass } from "./ingredientTable.columns";
import IngredientTableRow from "./IngredientTableRow";

const COLUMN_COUNT = INGREDIENT_TABLE_COLUMNS.length + 1; // + actions column

interface IngredientTableProps {
  rows: IngredientRowViewModel[];
  isLoading: boolean;
  isUserSupplier: boolean;
  onView: (row: IngredientRowViewModel) => void;
  onEdit: (row: IngredientRowViewModel) => void;
  onDelete: (row: IngredientRowViewModel) => void;
}

const ROW_SKELETON_COUNT = 5;

/**
 * Table shell styled after the Recipe Simulator list table
 * (src/screens/recipe_simulator/newList/components/RecipeListTable.tsx) —
 * same card container, fixed-width header cells tinted with the primary
 * color, and per-column skeleton rows while loading.
 */
const IngredientTable = ({
  rows,
  isLoading,
  isUserSupplier,
  onView,
  onEdit,
  onDelete
}: IngredientTableProps) => {
  return (
    <div className="tw-relative tw-min-h-[280px] tw-overflow-x-auto tw-rounded-lg tw-border tw-border-border tw-bg-white">
      <Table className="tw-w-full tw-table-fixed [&_td]:tw-px-4 [&_td]:tw-py-3 [&_th]:tw-px-4 [&_th]:tw-py-3">
        <TableHeader>
          <TableRow className="tw-border-b-0 tw-bg-primary/5 hover:tw-bg-primary/5 [&_th]:tw-bg-primary/5 [&_th]:tw-text-primary [&_th]:tw-font-semibold [&_th]:tw-align-middle">
            {INGREDIENT_TABLE_COLUMNS.map(column => (
              <TableHead key={column.key} className={ingredientTableColumnClass[column.key]}>
                {column.label}
              </TableHead>
            ))}
            <TableHead className={cn(ingredientTableColumnClass.actions, "tw-text-right")}>
              Actions
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {isLoading ? (
            [...Array(ROW_SKELETON_COUNT)].map((_, index) => (
              <TableRow key={index} className="tw-bg-white">
                {[...Array(COLUMN_COUNT)].map((__, cellIndex) => (
                  <TableCell key={cellIndex}>
                    <Skeleton className="tw-h-4 tw-w-full" />
                  </TableCell>
                ))}
              </TableRow>
            ))
          ) : rows.length === 0 ? (
            <TableRow className="tw-bg-white hover:tw-bg-white">
              <TableCell
                colSpan={COLUMN_COUNT}
                className="tw-py-12 tw-text-center tw-text-muted-foreground"
              >
                No ingredients found.
              </TableCell>
            </TableRow>
          ) : (
            rows.map((row, index) => (
              <IngredientTableRow
                key={`${row.ingredient_id}-${row.supplier_id ?? index}`}
                row={row}
                isUserSupplier={isUserSupplier}
                onView={onView}
                onEdit={onEdit}
                onDelete={onDelete}
              />
            ))
          )}
        </TableBody>
      </Table>
    </div>
  );
};

export default IngredientTable;
